import { useDebounce } from 'use-debounce';
import { Box, Card, Chip, Container, Dialog, Pagination, PaginationItem, Typography } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { validate } from '../../api/services/auth';
import { useNavigate } from 'react-router-dom';
import { pushNotification } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../utils/errorHandler';
import { ADMIN_CURRENT_NAME, ADMIN_STORAGE_KEY, DEFAULT_PAGE_LIMIT } from '../../utils/constants';
import Sidebar from '../../layouts/Sidebar/Sidebar';
import Header from '../../layouts/Header/Header';
import { PinnacleBasicTable } from '../../components/PinnacleBasicTable/PinnacleBasicTable';
import PinnacleDrawer from '../../components/PinnacleDrawer/PinnacleDrawer';
import { PinnacleSearchField } from '../../components/PinnacleSearchField/PinnacleSearchField';
import PinnacleConfirmDeleteItem from '../../components/PinnacleConfirmDeleteItem/PinnacleConfirmDeleteItem';
import { LoadingMode } from '../../api/interfaces/Common';
import { getSchools } from '../../api/services/school';
import { School } from '../../api/interfaces/School';
import { Examinee } from '../../api/interfaces/preboard/Examinee';
import { deleteExaminee, GetExamineeParams, getExaminees } from '../../api/services/preboard/examinee';
import EditExamineeDrawerContent from './partials/EditUserDrawerContent/EditExamineeDrawerContent';
import ExamineeExamDrawer from './partials/ExamineeExamDrawer/ExamineeExamDrawer';

const examineeColumn = [
    {
        key: 'firstName',
        label: 'FIRST NAME',
        render: (row: Examinee) => row.firstName ? row.firstName : '--'
    },
    {
        key: 'lastName',
        label: 'LAST NAME',
        sx: { minWidth: 150, maxWidth: 200 },
        render: (row: Examinee) => row.lastName ? row.lastName : '--'
    },
    { key: 'email', label: 'EMAIL ADDRESS' },
    {
        key: 'activeCodes',
        label: 'NO. of Activated Exam code',
        sx: { minWidth: 200, maxWidth: 250 },
        render: (row: Examinee) => row.activeCodes ? row.activeCodes : '--'
    },
    {
        key: 'examAttempts',
        label: 'NO. OF EXAMS TAKEN',
        sx: { minWidth: 150, maxWidth: 200 },
        render: (row: Examinee) => row.examAttempts ? row.examAttempts : '--'
    },
    {
        key: 'isExclusive',
        label: 'TYPE',
        render: (row: Examinee) => (
            <>
                {row.isExclusive && (
                    <Chip
                        label="active"
                        variant="filled"
                        color="error"
                        sx={{
                            backgroundColor: '#D7F2E7',
                            color: '#028A4F',
                            fontSize: '12px'
                        }}
                    />
                )}
            </>
        ),
        skipTooltip: true
    }
];

const PreboardExamineePage: React.FC = () => {
    const navigate = useNavigate();

    const [currentAdminName, setCurrentAdminName] = useState<string>('');
    const [schools, setSchools] = useState<School[]>([]); // In preparation to edit mode to supply the schools right ahead
    const [examinees, setExaminees] = useState<Examinee[]>([]);

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);

    // Search user with debounce
    const [searchUser, setSearchUser] = useState<string>('');
    const [debouncedSearchExaminee] = useDebounce(searchUser, 300);

    const [isSearching, setIsSearching] = useState(false);

    // Loading mode to handle different loading states
    const [loadingMode, setLoadingMode] = useState<LoadingMode>('none');

    // Drawable states for create
    const [currentDrawableState, setCurrentDrawableState] = useState<string>('');
    const [currentDrawableTitle, setCurrentDrawableTitle] = useState<string>('');

    // Set the selected user id for delete state
    const [selectedUserId, setSelectedUserId] = useState<string>('');
    const [selectedUserName, setSelectedUserName] = useState<string>('');
    const [selectedUserInfo, setSelectedUserInfo] = useState<Examinee | null>(null);
    // Use the correct type for the ref to access handleSubmit
    const editUserRef = useRef<{ handleSubmit: () => void } | null>(null);

    // Confirm delete dialog state
    const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState<boolean>(false);

    // Submit status state
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Handle the opening and closing of toggle
    const toggleDrawer = (
        open: boolean, state: | 'edit' | 'exams' | string,
        labelName?: string
    ) => () => {
        if (open) {
            setCurrentDrawableState(state);
            const title = state === 'edit' ? `Update User - ${labelName}` : '';
            setCurrentDrawableTitle(title);
        } else {
            setCurrentDrawableState('')
        }
    };

    const fetchUsers = useCallback(async (
        pageNumber = 1,
        pageLimit = DEFAULT_PAGE_LIMIT,
        keywordParam = debouncedSearchExaminee
    ) => {
        try {
            const params: GetExamineeParams = {
                page: pageNumber,
                limit: pageLimit,
                keyword: keywordParam || undefined
            };

            const usersResponse = await getExaminees(params);

            setExaminees(usersResponse.data.data);
            setPage(pageNumber);
            setTotalPages(usersResponse.data.totalPages || 0);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, [debouncedSearchExaminee]);

    const fetchSchools = useCallback(async () => {
        try {
            const schoolResponse = await getSchools();
            setSchools(schoolResponse.data);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        }
    }, []);

    const fetchSearchResults = useCallback(async (pageNumber = 1) => {
        try {
            setIsSearching(true);
            await fetchUsers(pageNumber, DEFAULT_PAGE_LIMIT, debouncedSearchExaminee);
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({ details: msg, type: NotificationTypes.Error });
                },
            });
            setIsSearching(false);
        } finally {
            setIsSearching(false);
        }
    }, [fetchUsers, debouncedSearchExaminee]);

    const disableActionButtons = loadingMode === 'deleting' || loadingMode === 'paginating' || loadingMode === 'submitting' || isSubmitting;

    const handleEdit = (selectedUser: Examinee) => {
        setSelectedUserId(selectedUser._id);
        setSelectedUserInfo(selectedUser);
        toggleDrawer(true, 'edit', selectedUser.email)();
    };

    // Open the dialog propmt to confirm
    const handleDelete = (user: Examinee) => {
        setSelectedUserId(user._id);
        setSelectedUserName(`${user.firstName} ${user.lastName} - ${user.email}`);
        setIsConfirmDeleteDialogOpen(true);
    };

    const handleExamineeExam = (selectedUser: Examinee) => {
        setSelectedUserId(selectedUser._id);
        setSelectedUserInfo(selectedUser);
        setSelectedUserName(`${selectedUser.firstName} ${selectedUser.lastName} List of Exams`);
        toggleDrawer(true, 'exam', selectedUser.email)();
    };

    const handleConfirmDelete = async () => {
        setIsSubmitting(true);
        try {
            const deleteResponse = await deleteExaminee(selectedUserId);
            setExaminees(prevUsers => prevUsers.filter(user => user._id !== selectedUserId));
            pushNotification({
                details: deleteResponse.message,
                type: NotificationTypes.Success,
            });
            setIsConfirmDeleteDialogOpen(false);
            setSelectedUserName('');
            setSelectedUserId('');
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setIsSubmitting(false);
            setLoadingMode('none');
        } finally {
            setIsSubmitting(false);
        }
    }

    const handlePageChange = async (newPage: number) => {
        try {
            // Do not fetch if the page is already the same
            if (newPage !== page) {
                setPage(newPage);
                setLoadingMode('paginating');
                await fetchUsers(newPage, DEFAULT_PAGE_LIMIT, searchUser);
            }
        } catch (error) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
            setLoadingMode('none');
        } finally {
            setLoadingMode('none');
        }
    }

    const handleUpdateUser = (updatedUser: Examinee, skipClose?: boolean) => {
        setExaminees((prev) =>
            prev.map((u) => (u._id === updatedUser._id ? updatedUser : u))
        );

        if (!skipClose) {
            setSelectedUserId('');
            setSelectedUserInfo(null);
            toggleDrawer(false, currentDrawableState)();
        }
    };

    const handleCloseDrawer = () => {
        if (!isSubmitting) {
            toggleDrawer(false, currentDrawableState)();
        }
    }

    const handleAttemptDeleted = () => {
        setExaminees(prevUsers =>
            prevUsers.map(user => {
                if (user._id === selectedUserId) {
                    return {
                        ...user,
                        examAttempts: user.examAttempts - 1
                    };
                }
                return user;
            })
        );
    };

    // Debouncing mechanism for search examinee
    useEffect(() => {
        if (debouncedSearchExaminee.trim() === '') return;

        const search = async () => {
            setLoadingMode('searching');
            setIsSearching(true);
            try {
                await fetchSearchResults();
            } finally {
                setIsSearching(false);
                setLoadingMode('none');
            }
        };

        search();
    }, [debouncedSearchExaminee, fetchSearchResults]);

    // Initial state when component load
    useEffect(() => {
        const run = async () => {
            const token =
                localStorage.getItem(ADMIN_STORAGE_KEY) ||
                sessionStorage.getItem(ADMIN_STORAGE_KEY);
            if (!token) {
                navigate('/login');
                return;
            }
            const cachedName = localStorage.getItem(ADMIN_CURRENT_NAME);
            if (cachedName) {
                setCurrentAdminName(cachedName);
            }
            try {
                setLoadingMode('fetching');
                const validateResponse = await validate();
                const firstName = validateResponse.data.firstName;
                if (!cachedName) {
                    localStorage.setItem(ADMIN_CURRENT_NAME, firstName);
                }

                setCurrentAdminName(firstName);
                await fetchUsers();
                await fetchSchools();
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setLoadingMode('none');
            }
        };
        run();
    }, [fetchUsers, fetchSchools, navigate]);

    const isEditOpen = currentDrawableState === 'edit';
    const isExamOpen = currentDrawableState === 'exam';

    return (
        <main className="main-content">
            <Sidebar />
            <Box
                component="section"
                sx={{
                    flexGrow: 1,
                    width: '100%',
                    overflowX: 'hidden'
                }}
            >
                <Container
                    maxWidth="lg"
                    sx={{
                        mt: { xs: 4, sm: 6 },
                        mb: 4,
                        px: { xs: 2, sm: 3 },
                    }}
                >
                    <Header title='Users' currentAdminName={currentAdminName} />

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 4,
                            flexWrap: 'wrap',
                            gap: 2,
                        }}
                    >
                        {/* Left side: Search */}
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <PinnacleSearchField
                                    id="searchUsers"
                                    label="Search examinee here..."
                                    value={searchUser}
                                    onChange={setSearchUser}
                                    isLoading={isSearching}
                                    disabled={disableActionButtons}
                                />
                            </Box>
                        </Box>
                    </Box>
                    <Card
                        elevation={0}
                        sx={{
                            borderRadius: '8px',
                            p: 3,
                            backgroundColor: '#fff',
                            boxShadow: '0px 4px 12px 8px rgba(211, 211, 211, 0.2)',
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            height: 'auto',
                        }}
                    >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Typography variant="body1" fontWeight={600}>
                                    List of Examinees
                                </Typography>
                            </Box>
                        </Box>


                        <Box sx={{ position: 'relative' }}>
                            <Box sx={{ opacity: isSearching ? 0.4 : 1 }}>
                                <PinnacleBasicTable
                                    columns={examineeColumn}
                                    rows={examinees}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    onPreboardExamineeExam={handleExamineeExam}
                                    headerColor="#5840BA"
                                    hasMultiSelect={false}
                                    disabledActions={disableActionButtons}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'right', mt: 2 }}>
                                    <Pagination
                                        count={totalPages}
                                        page={page}
                                        onChange={(_, newPage) => handlePageChange(newPage)}
                                        shape="rounded"
                                        renderItem={(item) => (
                                            <PaginationItem
                                                {...item}
                                                sx={{
                                                    border: "1px solid #D8D8DD",
                                                    color: "#818181",
                                                    "&.Mui-selected": {
                                                        backgroundColor: "#D8D8DD !important",
                                                        color: "#000 !important",
                                                        "&:hover": {
                                                            backgroundColor: "#D8D8DD",
                                                        },
                                                    },
                                                    "&:not(.Mui-selected)": {
                                                        color: "#818181",
                                                    },
                                                    "&.Mui-disabled": {
                                                        opacity: 0.4,
                                                    }
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                            {(isSearching || loadingMode === 'fetching' || loadingMode === 'paginating' || loadingMode === 'filtering' || loadingMode === 'submitting') && (
                                <CircularProgress
                                    size={24}
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)'
                                    }}
                                />
                            )}
                        </Box>
                    </Card>
                </Container>
            </Box>

            <PinnacleDrawer
                title={currentDrawableTitle}
                isDrawerOpen={isEditOpen}
                onClose={handleCloseDrawer}
                onSubmit={() => editUserRef.current?.handleSubmit()}
                onSubmitName={currentDrawableState === 'edit' ? 'UPDATE INFORMATION' : ''}
                isSubmitting={isSubmitting}
            >
                {currentDrawableState === 'edit' && (
                    <EditExamineeDrawerContent
                        ref={editUserRef}
                        schools={schools}
                        selectedUserId={selectedUserId}
                        selectedUserInfo={selectedUserInfo}
                        onClose={() => toggleDrawer(false, currentDrawableState)()}
                        onSuccess={handleUpdateUser}
                        setIsSubmitting={setIsSubmitting}
                        isSubmitting={isSubmitting}
                    />
                )}
            </PinnacleDrawer>

            <ExamineeExamDrawer
                isDrawerOpen={isExamOpen}
                onClose={handleCloseDrawer}
                examineeId={selectedUserId}
                examineeFullName={selectedUserName}
                activatedExamCodes={selectedUserInfo?.examCodes ?? []}
                onAttemptDeleted={handleAttemptDeleted}
            />

            <Dialog
                open={isConfirmDeleteDialogOpen}
                onClose={() => setIsConfirmDeleteDialogOpen(!isConfirmDeleteDialogOpen)}
                aria-labelledby="responsive-dialog-title"
                sx={{
                    borderRadius: '0 !important'
                }}
            >
                <PinnacleConfirmDeleteItem
                    title="Delete User"
                    itemToDelete={selectedUserName}
                    onClose={() => setIsConfirmDeleteDialogOpen(false)}
                    onDelete={handleConfirmDelete}
                    onCancel={() => setIsConfirmDeleteDialogOpen(false)}
                    isDeleting={isSubmitting}
                />
            </Dialog>
        </main>
    );
};

export default PreboardExamineePage;
